import React from 'react';
import { Line } from 'react-chartjs-2';
import { OpsFilterModel, OpsSummaryModel } from '../../../../shared/models/lead-ops.model';
import { getOpsSummary } from '../../../../shared/services/data/lead-ops.data';
import { STATUS_STYLE, VERTICAL_STYLE, styleFor } from '../../../shared/lead-visuals';
import { useOpsReport } from '../components/use-ops-report.hook';
import { OpsBars, OpsSection, OpsStat } from '../components/ops-section.component';

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const barTone = (map: Record<string, string>) => (key: string) => {
  // Reuse the chip palette so a status is the same colour wherever it appears
  // — a bar and a chip disagreeing about what "pending" looks like is the kind
  // of thing that quietly erodes trust in a dashboard.
  const chip = styleFor(map, key);
  return chip.split(' ')[0].replace('bg-', 'bg-');
};

export const StatusAndOverviewSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsSummaryModel>(getOpsSummary, filter);

  const integrity = data?.integrity;
  const hasDefect =
    !!integrity &&
    (!integrity.statusBalanced ||
      integrity.resolvedWithoutOutcome > 0 ||
      integrity.outcomesOutsideResolved > 0);

  return (
    <OpsSection
      title="Overview"
      subtitle="Everything below respects the filters above, and your own lead scope."
      loading={loading}
      error={error}
      empty={!!data && data.totalLeads === 0}
      onRetry={reload}
    >
      {data ? (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OpsStat label="Leads" value={data.totalLeads} />
            <OpsStat
              label="Resolved"
              value={data.byStatus.find((row) => row.key === 'resolved')?.count ?? 0}
            />
            <OpsStat
              label="Open"
              value={data.byStatus
                .filter((row) => ['new', 'pending', 'reopened', 'not_reachable'].includes(row.key))
                .reduce((sum, row) => sum + row.count, 0)}
            />
            <OpsStat
              label="Verticals"
              value={data.byVertical.length}
              hint={data.byVertical.map((row) => titleCase(row.key)).join(', ')}
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By status</h3>
              <OpsBars rows={data.byStatus} label={titleCase} tone={barTone(STATUS_STYLE)} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By vertical</h3>
              <OpsBars rows={data.byVertical} label={titleCase} tone={barTone(VERTICAL_STYLE)} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">By resolution</h3>
              <OpsBars rows={data.byResolution} label={titleCase} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">
                By marketing channel
              </h3>
              <OpsBars rows={data.byChannel} label={titleCase} />
            </div>
          </div>

          {data.dailyTrend.length ? (
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">Daily volume</h3>
              <div className="h-56">
                <Line
                  data={{
                    labels: data.dailyTrend.map((row) => row.date),
                    datasets: [
                      {
                        label: 'Leads',
                        data: data.dailyTrend.map((row) => row.total),
                        borderColor: '#237c58',
                        backgroundColor: 'rgba(35,124,88,0.15)',
                        tension: 0.3
                      },
                      {
                        label: 'Resolved',
                        data: data.dailyTrend.map((row) => row.resolved),
                        borderColor: '#f4603d',
                        backgroundColor: 'rgba(244,96,61,0.15)',
                        tension: 0.3
                      }
                    ]
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom' } }
                  }}
                />
              </div>
            </div>
          ) : null}

          {/*
            The dashboard checking its own arithmetic. Status is mutually
            exclusive so it must sum to the total, and a resolution only means
            something on a resolved lead. Shown only when something is off —
            a permanent green tick trains people to stop reading it.
          */}
          {hasDefect && integrity ? (
            <div className="rounded-xl bg-creams-300 px-4 py-3 text-xs text-creams-900">
              <strong>These numbers do not reconcile.</strong>{' '}
              {!integrity.statusBalanced
                ? `Status counts sum to ${integrity.statusSum} but there are ${integrity.totalLeads} leads. `
                : ''}
              {integrity.resolvedWithoutOutcome > 0
                ? `${integrity.resolvedWithoutOutcome} resolved without an outcome. `
                : ''}
              {integrity.outcomesOutsideResolved > 0
                ? `${integrity.outcomesOutsideResolved} carry an outcome without being resolved.`
                : ''}
            </div>
          ) : null}
        </div>
      ) : null}
    </OpsSection>
  );
};
